import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IPaymentMethod } from '@/providers';
import { PaymentProvider } from '@/providers';
import type { IQuote } from '@/types';

interface IUsePaymentMethodsReturn {
  getPaymentUrl: (methodId: string) => string;
  isLoading: boolean;
  isPaymentEnabled: boolean;
  paymentMethods: IPaymentMethod[];
  selectedPaymentMethod: string | undefined;
  setSelectedPaymentMethod: (id: string) => void;
}

/**
 * Hook to fetch and manage payment methods for a quote
 * @param {string | undefined} id - The ID of the quote
 * @param {IQuote | undefined} quote - The quote object
 * @returns {object}
 */
export const usePaymentMethods = (
  id: string | undefined,
  quote: IQuote | undefined
): IUsePaymentMethodsReturn => {
  const { i18n } = useTranslation();
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [isLoading, setLoading] = useState(false);

  const isQuotePending = quote?.status === 'QUOTE_PENDING';

  useEffect(() => {
    const fetchPaymentMethods = async (): Promise<void> => {
      if (isQuotePending && id && quote.paymentProviders) {
        setLoading(true);

        try {
          const locale = i18n.language.replace('-', '_');
          const provider = new PaymentProvider(id, quote.paymentProviders);
          const methods = await provider.listMethods(locale);
          setPaymentMethods(methods);
        } catch (error) {
          console.error('Error fetching payment methods:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    void fetchPaymentMethods();
  }, [isQuotePending, id, quote?.paymentProviders, i18n.language]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>();

  useEffect(() => {
    if (paymentMethods.length === 1 && !selectedPaymentMethod) {
      setSelectedPaymentMethod(paymentMethods[0].id);
    }
  }, [paymentMethods, selectedPaymentMethod]);

  const getPaymentUrl = (methodId: string): string => {
    if (!id || !quote?.paymentProviders) return '#';
    const provider = new PaymentProvider(id, quote.paymentProviders);
    return provider.generatePaymentUrl(methodId);
  };

  return {
    paymentMethods,
    isLoading,
    getPaymentUrl,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    isPaymentEnabled: isQuotePending && (paymentMethods.length > 0 || isLoading),
  };
};
